import { useState, useEffect, useCallback } from 'react';
import { User, Shield, Building2, FileText, Download, AlertCircle, Clock, Check, X, MapPin, Briefcase, CreditCard, Lock } from 'lucide-react';
import KYCModal from './KYCModal';
import ChangePasswordModal from '@/components/shared/ChangePasswordModal';
import { api } from '@/utils/api';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

const KYC_STATUS_CONFIG = {
  NOT_STARTED: { 
    label: 'Not Started', 
    color: 'bg-gray-500/10 border-gray-500/30 text-gray-400',
    icon: AlertCircle,
    message: 'Complete your KYC to start investing'
  },
  IN_PROGRESS: { 
    label: 'In Progress', 
    color: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
    icon: Clock,
    message: 'Complete all steps to submit your KYC'
  },
  SUBMITTED: { 
    label: 'Pending Verification', 
    color: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    icon: Clock,
    message: 'Your KYC is being reviewed by our team'
  },
  VERIFIED: { 
    label: 'Verified', 
    color: 'bg-green-500/10 border-green-500/30 text-green-400',
    icon: Check,
    message: 'Your identity has been verified'
  },
  REJECTED: { 
    label: 'Rejected', 
    color: 'bg-red-500/10 border-red-500/30 text-red-400',
    icon: X,
    message: 'Please review and resubmit your KYC'
  }
};

// Profile data loaded from API (see fetchProfile)

export default function InvestorProfileTab({ user }) {
  const [activeSection, setActiveSection] = useState('personal');
  const [kycData, setKycData] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [sameAddress, setSameAddress] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;
    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await api.get(`/investor/${user.id}/profile`);
      const p = res.data;
      setProfileData({
        name: p.personal?.name || user?.name || '',
        email: p.personal?.email || user?.email || '',
        phone: p.personal?.phone || '',
        dob: p.personal?.dateOfBirth || '',
        occupation: p.personal?.occupation || '',
        currentAddress: p.currentAddress || {},
        permanentAddress: p.permanentAddress || {},
        sameAddress: false,
        nominee: p.nominee || {},
        identity: {
          pan: p.kyc?.panVerified ? p.personal?.pan : (p.personal?.pan || ''),
          panVerified: p.kyc?.panVerified || false,
          aadhaar: '',
          aadhaarVerified: p.kyc?.aadhaarVerified || false,
        },
        bankDetails: p.bankDetails || {},
        taxInfo: p.taxInfo || {},
      });
    } catch (e) {
      console.error('Failed to load profile:', e);
      setProfileError('Failed to load profile data.');
    } finally {
      setProfileLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      fetchKYCData();
      fetchProfile();
    }
  }, [user?.id, fetchProfile]);

  const fetchKYCData = async () => {
    setKycLoading(true);
    try {
      const statusResponse = await fetch(`${API_URL}/api/investor/kyc/status/${user.id}`);
      const statusData = await statusResponse.json();
      
      if (statusData.status !== 'NOT_STARTED') {
        try {
          const profileResponse = await fetch(`${API_URL}/api/investor/kyc/profile/${user.id}`);
          const pd = await profileResponse.json();
          setKycData({ ...statusData, ...pd });
        } catch {
          setKycData(statusData);
        }
      } else {
        setKycData(statusData);
      }
    } catch {
      setKycData({ status: 'NOT_STARTED', canInvest: false });
    } finally {
      setKycLoading(false);
    }
  };

  // Merge real KYC data with mock profile
  const displayName = user?.name || profileData.name;
  const displayEmail = user?.email || profileData.email;
  const displayPhone = kycData?.personalDetails?.phone || profileData.phone;
  const displayDob = kycData?.personalDetails?.dob || profileData.dob;

  const bankAccounts = kycData?.bankDetails ? [
    { 
      id: 1, 
      bank: kycData.bankDetails.bankName || 'Bank', 
      accountNumber: kycData.bankDetails.accountNumber || '****0000', 
      ifsc: kycData.bankDetails.ifsc || 'N/A', 
      isPrimary: true,
      verified: kycData.verificationDetails?.bankVerified
    }
  ] : [];

  const documents = [
    { id: 1, name: 'LLP Agreement - Skyline Tower', type: 'Agreement', date: '2025-12-15', unitId: 'UNIT001' },
    { id: 2, name: 'LLP Agreement - Marina Bay', type: 'Agreement', date: '2025-12-20', unitId: 'UNIT002' },
    { id: 3, name: 'Tax Certificate FY 2025-26', type: 'Tax Document', date: '2026-01-01', unitId: null }
  ];

  const sections = [
    { id: 'personal', label: 'Personal Details', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'nominee', label: 'Nominee', icon: Shield },
    { id: 'kyc', label: 'KYC & Verification', icon: Shield },
    { id: 'identity', label: 'Identity Documents', icon: CreditCard },
    { id: 'bank', label: 'Bank Accounts', icon: Building2 },
    { id: 'tax', label: 'Tax Information', icon: Briefcase },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  const kycStatus = kycData?.status || 'NOT_STARTED';
  const statusConfig = KYC_STATUS_CONFIG[kycStatus] || KYC_STATUS_CONFIG.NOT_STARTED;
  const StatusIcon = statusConfig.icon;

  const inputCls = "w-full bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-white/30 focus:outline-none transition-colors";
  const labelCls = "text-xs text-gray-600 uppercase tracking-wider mb-2 block";

  if (profileLoading && kycLoading) return <div className="p-6 md:p-10"><DashboardSkeleton cards={0} rows={8} cols={3} /></div>;
  if (profileError) return <div className="p-6 md:p-10"><ErrorBanner message={profileError} onRetry={fetchProfile} /></div>;

  return (
    <div className="p-6 md:p-10" data-testid="investor-profile">
      <div className="mb-8">
        <h1 className="text-2xl font-light text-white mb-2">Profile</h1>
        <p className="text-sm text-gray-500">Manage your account, identity, and preferences</p>
      </div>

      <div className="grid md:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full text-left px-4 py-3 text-sm border-l-2 transition-all flex items-center gap-2 ${
                activeSection === section.id ? 'border-[#4ADE80] bg-white/5 text-white' : 'border-transparent text-gray-500 hover:text-white'
              }`}
              data-testid={`profile-section-${section.id}`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
              {section.id === 'kyc' && kycStatus !== 'VERIFIED' && (
                <span className="ml-auto w-2 h-2 rounded-full bg-yellow-500 inline-block" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="md:col-span-3">
          {/* Personal Details */}
          {activeSection === 'personal' && (
            <div className="border border-white/10 p-8" data-testid="section-personal">
              <h2 className="text-lg text-white mb-6">Personal Information</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Full Name</label>
                  <input value={displayName} className={inputCls} readOnly data-testid="input-name" />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <div className="flex gap-2">
                    <input value={displayEmail} className={`flex-1 ${inputCls}`} readOnly data-testid="input-email" />
                    <button className="px-4 border border-white/10 text-gray-500 hover:text-white text-sm">Change</button>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input value={displayPhone} className={inputCls} readOnly data-testid="input-phone" />
                </div>
                <div>
                  <label className={labelCls}>Date of Birth</label>
                  <input value={displayDob} className={inputCls} readOnly data-testid="input-dob" />
                </div>
                <div>
                  <label className={labelCls}>Occupation</label>
                  <input value={profileData.occupation} className={inputCls} readOnly data-testid="input-occupation" />
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          {activeSection === 'address' && (
            <div className="border border-white/10 p-8" data-testid="section-address">
              <h2 className="text-lg text-white mb-6">Address Details</h2>

              {/* Current Address */}
              <div className="mb-8">
                <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4">Current Address</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className={labelCls}>Address Line</label>
                    <input value={profileData.currentAddress.line1} className={inputCls} readOnly data-testid="input-current-address" />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input value={profileData.currentAddress.city} className={inputCls} readOnly />
                  </div>
                  <div>
                    <label className={labelCls}>State</label>
                    <input value={profileData.currentAddress.state} className={inputCls} readOnly />
                  </div>
                  <div>
                    <label className={labelCls}>Pincode</label>
                    <input value={profileData.currentAddress.pincode} className={inputCls} readOnly />
                  </div>
                </div>
              </div>

              {/* Same Address Toggle */}
              <div className="flex items-center gap-3 mb-8 p-4 bg-white/5 border border-white/10">
                <input
                  type="checkbox"
                  checked={sameAddress}
                  onChange={() => setSameAddress(!sameAddress)}
                  className="w-4 h-4 accent-[#4ADE80]"
                  data-testid="same-address-toggle"
                />
                <label className="text-sm text-gray-400">Permanent address is same as current address</label>
              </div>

              {/* Permanent Address */}
              {!sameAddress && (
                <div>
                  <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4">Permanent Address</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="col-span-2">
                      <label className={labelCls}>Address Line</label>
                      <input value={profileData.permanentAddress.line1} className={inputCls} readOnly data-testid="input-permanent-address" />
                    </div>
                    <div>
                      <label className={labelCls}>City</label>
                      <input value={profileData.permanentAddress.city} className={inputCls} readOnly />
                    </div>
                    <div>
                      <label className={labelCls}>State</label>
                      <input value={profileData.permanentAddress.state} className={inputCls} readOnly />
                    </div>
                    <div>
                      <label className={labelCls}>Pincode</label>
                      <input value={profileData.permanentAddress.pincode} className={inputCls} readOnly />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Nominee */}
          {activeSection === 'nominee' && (
            <div className="border border-white/10 p-8" data-testid="section-nominee">
              <h2 className="text-lg text-white mb-2">Nominee Details</h2>
              <p className="text-sm text-gray-500 mb-6">Nominee information for your investments. Required for Zoho Sign documents.</p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Nominee Name</label>
                  <input value={profileData.nominee.name} className={inputCls} readOnly data-testid="input-nominee-name" />
                </div>
                <div>
                  <label className={labelCls}>Relationship</label>
                  <input value={profileData.nominee.relationship} className={inputCls} readOnly data-testid="input-nominee-relationship" />
                </div>
                <div>
                  <label className={labelCls}>Date of Birth</label>
                  <input value={profileData.nominee.dob} className={inputCls} readOnly data-testid="input-nominee-dob" />
                </div>
                <div>
                  <label className={labelCls}>Contact Number</label>
                  <input value={profileData.nominee.contact} className={inputCls} readOnly data-testid="input-nominee-contact" />
                </div>
              </div>
            </div>
          )}

          {/* KYC & Verification */}
          {activeSection === 'kyc' && (
            <div className="border border-white/10 p-8" data-testid="section-kyc">
              <h2 className="text-lg text-white mb-6">KYC & Verification</h2>
              
              {kycLoading ? (
                <div className="animate-pulse bg-white/5 h-24" />
              ) : (
                <>
                  <div className={`border p-6 mb-8 flex items-center gap-4 ${statusConfig.color}`}>
                    <StatusIcon className="w-8 h-8" />
                    <div className="flex-1">
                      <p className="text-white font-medium mb-1">{statusConfig.label}</p>
                      <p className="text-sm text-gray-400">{statusConfig.message}</p>
                      {kycStatus === 'REJECTED' && kycData?.rejectionReason && (
                        <p className="text-sm text-red-300 mt-2">Reason: {kycData.rejectionReason}</p>
                      )}
                      {kycData?.verifiedAt && (
                        <p className="text-sm text-gray-400 mt-1">
                          Verified on {new Date(kycData.verifiedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {(kycStatus === 'NOT_STARTED' || kycStatus === 'REJECTED') && (
                      <button 
                        onClick={() => setShowKYCModal(true)}
                        className="bg-white text-black px-6 py-2 text-sm font-semibold hover:bg-gray-200 transition-colors"
                        data-testid="start-kyc-btn"
                      >
                        {kycStatus === 'REJECTED' ? 'Resubmit KYC' : 'Start KYC'}
                      </button>
                    )}
                  </div>

                  {kycStatus === 'NOT_STARTED' && (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">You haven't completed your KYC yet</p>
                      <button 
                        onClick={() => setShowKYCModal(true)}
                        className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black px-8 py-3 font-semibold text-sm uppercase tracking-wider"
                        data-testid="complete-kyc-btn"
                      >
                        Complete KYC Now
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Identity Documents */}
          {activeSection === 'identity' && (
            <div className="border border-white/10 p-8" data-testid="section-identity">
              <h2 className="text-lg text-white mb-2">Identity Documents</h2>
              <p className="text-sm text-gray-500 mb-6">Government-issued identity proofs linked to your account</p>
              <div className="space-y-6">
                <div>
                  <label className={labelCls}>PAN Card</label>
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 px-4 py-3">
                    <p className="font-mono text-white" data-testid="display-pan">
                      {kycData?.personalDetails?.pan || profileData.identity.pan}
                    </p>
                    {(kycData?.verificationDetails?.panVerified || profileData.identity.panVerified) ? (
                      <span className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> Verified</span>
                    ) : (
                      <span className="text-xs text-gray-500">Not verified</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Aadhaar Number</label>
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 px-4 py-3">
                    <p className="font-mono text-white" data-testid="display-aadhaar">
                      {kycData?.personalDetails?.aadhar ? `XXXX-XXXX-${kycData.personalDetails.aadhar.slice(-4)}` : `XXXX-XXXX-${profileData.identity.aadhaar.slice(-4)}`}
                    </p>
                    {(kycData?.verificationDetails?.aadharVerified || profileData.identity.aadhaarVerified) ? (
                      <span className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> Verified</span>
                    ) : (
                      <span className="text-xs text-gray-500">Not verified</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Passport Number</label>
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 px-4 py-3">
                    <p className="font-mono text-white" data-testid="display-passport">{profileData.identity.passport || 'Not provided'}</p>
                    <span className="text-xs text-gray-500">Optional</span>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Driving License</label>
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 px-4 py-3">
                    <p className="font-mono text-white" data-testid="display-dl">{profileData.identity.drivingLicense || 'Not provided'}</p>
                    <span className="text-xs text-gray-500">Optional</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bank Accounts */}
          {activeSection === 'bank' && (
            <div className="border border-white/10 p-8" data-testid="section-bank">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg text-white">Bank Accounts</h2>
                {kycStatus === 'VERIFIED' && (
                  <button className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black px-6 py-2 text-sm font-semibold uppercase tracking-wider">
                    Add Account
                  </button>
                )}
              </div>
              
              {bankAccounts.length > 0 ? (
                bankAccounts.map((bank) => (
                  <div key={bank.id} className="border border-white/10 p-6 mb-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-white font-medium mb-2">{bank.bank}</p>
                        <p className="font-mono text-sm text-gray-400">{bank.accountNumber}</p>
                        <p className="text-xs text-gray-600 mt-1">IFSC: {bank.ifsc}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {bank.isPrimary && (
                          <span className="text-xs bg-[#4ADE80]/10 text-[#4ADE80] px-3 py-1 border border-[#4ADE80]/30">Primary</span>
                        )}
                        {bank.verified && (
                          <span className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> Verified</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No bank accounts added yet</p>
                  {kycStatus !== 'VERIFIED' && (
                    <p className="text-sm text-gray-500">Complete your KYC to add bank accounts</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tax Information */}
          {activeSection === 'tax' && (
            <div className="border border-white/10 p-8" data-testid="section-tax">
              <h2 className="text-lg text-white mb-6">Tax Information</h2>
              <p className="text-gray-500 mb-6">Download tax certificates and Form 16A for your investments</p>
              <button className="border border-white/10 hover:border-white/20 px-6 py-3 text-white text-sm uppercase tracking-wider flex items-center gap-2" data-testid="download-tax-cert">
                <Download className="w-4 h-4" />
                Download Annual Tax Certificate
              </button>
            </div>
          )}

          {/* Security */}
          {activeSection === 'security' && (
            <div className="border border-white/10 p-8" data-testid="section-security">
              <h2 className="text-lg text-white mb-6">Security</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-5 border border-white/10 bg-white/5">
                  <div>
                    <p className="text-white font-medium">Change Password</p>
                    <p className="text-sm text-gray-500 mt-1">Update your account password</p>
                  </div>
                  <button
                    onClick={() => setShowChangePassword(true)}
                    className="bg-white text-black px-6 py-2 text-sm font-semibold hover:bg-gray-200 transition-colors"
                    data-testid="investor-change-pw-btn"
                  >
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          {activeSection === 'documents' && (
            <div className="border border-white/10 p-8" data-testid="section-documents">
              <h2 className="text-lg text-white mb-6">Documents</h2>
              <div className="border border-white/10 overflow-hidden">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 bg-white/5">
                  <div className="col-span-5"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Document</p></div>
                  <div className="col-span-2"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Type</p></div>
                  <div className="col-span-2"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Date</p></div>
                  <div className="col-span-2"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Unit ID</p></div>
                  <div className="col-span-1"></div>
                </div>
                {documents.map((doc) => (
                  <div key={doc.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 hover:bg-white/5" data-testid={`doc-row-${doc.id}`}>
                    <div className="col-span-5 flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <p className="text-sm text-white">{doc.name}</p>
                    </div>
                    <div className="col-span-2"><p className="text-sm text-gray-400">{doc.type}</p></div>
                    <div className="col-span-2"><p className="text-sm text-white">{new Date(doc.date).toLocaleDateString()}</p></div>
                    <div className="col-span-2"><p className="text-xs text-gray-600 font-mono">{doc.unitId || '-'}</p></div>
                    <div className="col-span-1 flex justify-end">
                      <button className="text-xs text-[#4ADE80] hover:text-[#4ADE80]/80 uppercase">Download</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <KYCModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
        userId={user?.id}
        onComplete={() => {
          setShowKYCModal(false);
          fetchKYCData();
        }}
      />
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
}
